/**
 * Analytics Utility
 * Simple event tracking that can be extended to use PostHog, Google Analytics, etc.
 */

import posthog from "posthog-js";
import type { EventProperties } from "../shared/types/utils.types";

class Analytics {
  private initialized = false;
  private userId: string | null = null;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Initialize tracking services
    if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      this.initPostHog();
    }

    if (import.meta.env.VITE_GA_ID) {
      this.initGoogleAnalytics();
    }
  }

  private initPostHog() {
    if (typeof window === "undefined") return;

    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string, {
      api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      defaults: "2025-05-24",
      capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
      debug: import.meta.env.MODE === "development",
    });

    // Expose to window for manual tracking
    window.posthog = posthog;
  }

  private initGoogleAnalytics() {
    // Google Analytics initialization
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("config", import.meta.env.VITE_GA_ID);
    }
  }

  identify(userId: string, traits?: Record<string, any>) {
    this.userId = userId;
    // Identify user in tracking services
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.identify(userId, traits);
    }
  }

  reset() {
    this.userId = null;
    if (typeof window !== "undefined" && window.posthog) {
      window.posthog.reset();
    }
  }

  track(eventName: string, properties?: EventProperties) {
    if (!this.initialized) {
      this.init();
    }

    const eventData = {
      event: eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      },
      userId: this.userId,
    };

    // Send to tracking services
    if (import.meta.env.PROD) {
      // PostHog
      if (window.posthog) {
        window.posthog.capture(eventName, properties);
      }

      // Google Analytics
      if (window.gtag) {
        window.gtag("event", eventName, properties);
      }

      // Custom endpoint
      if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
        fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventData),
        }).catch(() => {
          // Silently fail if analytics service is unavailable
        });
      }
    } else {
      // Development - log events
      console.log("[Analytics]", eventName, properties);
    }
  }

  page(pageName: string, properties?: EventProperties) {
    this.track("page_view", {
      page_name: pageName,
      ...properties,
    });
  }

  // ============================================================
  // Pre-defined events for consistency
  // ============================================================

  // Auth events
  signUp(method: "email" | "google") {
    this.track("user_signed_up", { method });
  }

  signIn(method: "email" | "google") {
    this.track("user_signed_in", { method });
  }

  signOut() {
    this.track("user_signed_out");
    this.reset();
  }

  // Onboarding events
  onboardingStarted(userType: string) {
    this.track("onboarding_started", { user_type: userType });
  }

  onboardingCompleted(userType: string) {
    this.track("onboarding_completed", { user_type: userType });
  }

  // Subscription events
  subscriptionViewed(plan: string) {
    this.track("subscription_viewed", { plan });
  }

  subscriptionStarted(plan: string, price: number) {
    this.track("subscription_started", { plan, price });
  }

  subscriptionCancelled(plan: string) {
    this.track("subscription_cancelled", { plan });
  }

  // Lawyer features
  documentCreated(type: string) {
    this.track("document_created", { document_type: type });
  }

  caseCreated() {
    this.track("case_created");
  }

  consultationRequested() {
    this.track("consultation_requested");
  }

  clientAdded() {
    this.track("client_added");
  }

  aiReviewUsed() {
    this.track("ai_review_used");
  }

  // Profile events
  profileShared(
    platform: "linkedin" | "whatsapp" | "twitter" | "email" | "copy"
  ) {
    this.track("profile_shared", { platform });
  }

  publicConsultationReceived() {
    this.track("public_consultation_received");
  }
}

export const analytics = new Analytics();

// Initialize on load
if (typeof window !== "undefined") {
  analytics.init();
}

// Extend Window interface
declare global {
  interface Window {
    posthog?: typeof posthog;
    gtag?: (...args: any[]) => void;
  }
}
